import type { AssetResponse, VocabularyResponse } from '../../api';
import { METADATA_FIELD_LABELS } from '../../constants/metadataFields';
import { UNKNOWN_VALUES } from '../../constants/taxonomy';

/**
 * What linking a source image will do to the assets being linked.
 *
 * Pure and separate from `SourceImageSection` because it is the bit worth
 * testing: the component only renders the answer, but getting the answer wrong
 * means either nagging about a link that changes nothing, or overwriting
 * someone's work without asking.
 *
 * The copy itself happens on the server — see `INHERITED_FROM_SOURCE` in
 * `apps/asset-library/backend/src/core/asset_types.py`, which this list
 * mirrors. The frontend only needs to know which fields to warn about.
 */

/**
 * Fields a derivative takes from its source image.
 *
 * `copy_elements` and `platform_rights` are absent for the same reason as on
 * the backend: a resize carries its own copy and its own platform clearances.
 * `source_agency` is absent because no kind cut from a source owns one.
 */
export const INHERITED_FROM_SOURCE = [
  'expiry_rights',
  'nameplate',
  'carline_code',
  'business_unit',
  'tier',
  'ai_indicator',
  // The licence travels with the shoot: a crop cannot be cleared for more
  // channels or markets than its hero. Mirrors `INHERITED_FROM_SOURCE`.
  'channel_rights',
  'country_rights',
  'domain',
] as const;

export type InheritedField = (typeof INHERITED_FROM_SOURCE)[number];

/**
 * AI disclosure, weakest to strongest.
 *
 * An explicit order, never a sort: these are raw strings and alphabetically
 * `ai_enhanced` < `ai_generated` < `no_ai`, so `max()` over them returns "no
 * AI" for a pair that includes AI — the worst answer, reached by the most
 * natural-looking line of code. Mirrors `AI_INDICATOR_STRENGTH` on the backend.
 */
const AI_INDICATOR_STRENGTH = ['no_ai', 'ai_enhanced', 'ai_generated'];

/** What the country-rights intersection needs from the vocabulary. */
export type CountryVocabulary = Pick<VocabularyResponse, 'countries' | 'regions'>;

/**
 * A value as it appears on an asset. The vehicle lists, `tier` and the rights
 * are lists; everything else is a scalar, and both shapes flow through the
 * resolver and the conflict check.
 */
type InheritedValue = string | string[] | null;

const isBlank = (value: InheritedValue) => (Array.isArray(value) ? value.length === 0 : !value);

/** Identity for equality. Tier is a list, so compare by content, not order. */
const key = (value: InheritedValue) =>
  Array.isArray(value) ? JSON.stringify([...value].sort()) : value;

/**
 * Identity for *agreement between sources*, which is `key` plus case-folding.
 *
 * Separate from `key` because the server compares values after normalising
 * them to a canonical spelling: two heroes holding the same value written in
 * different case agree, and reading them as a conflict would inherit nothing.
 */
const agreementKey = (value: InheritedValue) =>
  Array.isArray(value)
    ? JSON.stringify([...value].map((entry) => entry.trim().toLowerCase()).sort())
    : value?.trim().toLowerCase();

/** Every country a `country_rights` list reaches. Mirrors `countries_covered`. */
function countriesCovered(tokens: string[], vocabulary: CountryVocabulary): string[] {
  const members = new Map(vocabulary.regions.map((region) => [region.value, region.countries]));
  const known = new Set(vocabulary.countries.map((country) => country.value));
  return [
    ...new Set(tokens.flatMap((token) => members.get(token) ?? (known.has(token) ? [token] : []))),
  ];
}

/** Every entry any list holds, once, in first-seen order. Mirrors `_union`. */
const unionEntries = (lists: string[][]) => [...new Set(lists.flat())];

/**
 * The lists that say *which* shoots an asset is cut from, so a composite
 * carries all of them. Mirrors `UNIONED` on the backend.
 */
const UNIONED: ReadonlySet<InheritedField> = new Set(['nameplate', 'carline_code', 'business_unit']);

/** The entries every list holds, in the order the first list holds them. */
const commonEntries = (lists: string[][]) =>
  lists[0].filter((item) => lists.slice(1).every((other) => other.includes(item)));

/**
 * The channels every source is licensed for, unless one source's are unknown,
 * which makes the composite's unknown too. Mirrors `_shared_channel_rights`.
 */
function sharedChannelRights(lists: string[][]): string[] {
  const unknown = UNKNOWN_VALUES.channel_rights;
  return lists.some((items) => items.includes(unknown)) ? [unknown] : commonEntries(lists);
}

/**
 * The markets every source is licensed for. Mirrors `_shared_country_rights`.
 *
 * Intersected by coverage, so `eu5` and `DE, FR, PL` share Germany and France.
 * A region every source picked stays a token; one only some picked is spelled
 * out as the countries it shares with the others. The first source's entries
 * keep their order, so one source resolves to its own list.
 */
function sharedCountryRights(lists: string[][], vocabulary: CountryVocabulary): string[] {
  const coverage = lists.map((items) => new Set(countriesCovered(items, vocabulary)));
  const covered = new Set([...coverage[0]].filter((c) => coverage.every((set) => set.has(c))));
  const regionTokens = new Set(vocabulary.regions.map((region) => region.value));
  const regions = new Set(commonEntries(lists).filter((item) => regionTokens.has(item)));
  const kept = lists[0].filter((item) => regions.has(item) || covered.has(item));
  const listed = new Set(countriesCovered(kept, vocabulary));
  const rest = vocabulary.countries
    .map((country) => country.value)
    .filter((country) => covered.has(country) && !listed.has(country));
  return [...kept, ...rest];
}

/**
 * What the server will land on each inherited field, given the sources picked.
 *
 * Mirrors `resolve_inheritance` in
 * `apps/asset-library/backend/src/api/inheritance.py`. It is duplicated rather
 * than fetched because this runs to phrase a question *before* the PATCH is
 * sent — there is nothing to ask the server for yet. Five rules:
 *
 *  - **Agree or blank** (`tier`, `domain`) — one distinct value inherits,
 *    disagreement inherits nothing. Compared case-insensitively, because the
 *    server compares normalised values: warning about a conflict the server
 *    will not have is a question the user cannot act on.
 *  - **Union** (`nameplate`, `carline_code`, `business_unit`) — every entry
 *    any source holds, once, first-seen order. A composite of a Puma hero and
 *    a Kuga hero is a Puma-and-Kuga asset. The three are independent.
 *  - **Narrowest wins** (`expiry_rights`) — the earliest date. Blanking a
 *    conflicting expiry would read as unrestricted.
 *  - **Intersection** (`channel_rights`, `country_rights`) — what every source
 *    shares; nothing shared inherits nothing. Countries by what they cover,
 *    which is why the vocabulary's region members are needed. One source with
 *    unknown channel rights makes the composite's unknown.
 *  - **Strongest wins** (`ai_indicator`) — blanking would under-disclose.
 *
 * Empty is not disagreement: a source with nothing in a field is filtered out
 * before comparing, so two sources that agree still inherit when a third is
 * blank. Expiry and the rights lists are the exception: the server refuses with
 * a 422 when only some sources have them (skipping one would widen the licence), and
 * that message reaches the user as the link's error, so this does not repeat it.
 */
export function resolveInherited(
  sourceImages: AssetResponse[],
  vocabulary: CountryVocabulary,
): Partial<Record<InheritedField, InheritedValue>> {
  const resolved: Partial<Record<InheritedField, InheritedValue>> = {};

  for (const field of INHERITED_FROM_SOURCE) {
    const values = sourceImages
      .map((source) => source[field])
      .filter((value): value is NonNullable<InheritedValue> => !isBlank(value ?? null));
    if (values.length === 0) continue;

    if (field === 'expiry_rights') {
      // ISO dates, so lexicographic order is chronological order.
      resolved[field] = (values as string[]).reduce((a, b) => (a < b ? a : b));
    } else if (field === 'ai_indicator') {
      resolved[field] = (values as string[]).reduce((a, b) =>
        AI_INDICATOR_STRENGTH.indexOf(b) > AI_INDICATOR_STRENGTH.indexOf(a) ? b : a,
      );
    } else if (UNIONED.has(field)) {
      resolved[field] = unionEntries(values as string[][]);
    } else if (field === 'channel_rights' || field === 'country_rights') {
      const lists = values as string[][];
      const shared =
        field === 'channel_rights'
          ? sharedChannelRights(lists)
          : sharedCountryRights(lists, vocabulary);
      if (shared.length > 0) resolved[field] = shared;
    } else if (new Set(values.map(agreementKey)).size === 1) {
      resolved[field] = values[0];
    }
  }

  return resolved;
}

/**
 * Which inherited fields the link would overwrite with something different.
 *
 * A field only counts when the asset already has a value, the resolved value
 * across the chosen sources is non-empty, and the two disagree. Empty on either
 * side is not a conflict: filling a blank is the whole point, and the server
 * skips a blank source rather than writing null.
 *
 * Compares against the *resolved* value rather than each source in turn. With
 * several sources what lands is not any one of them — the earliest expiry, the
 * strongest AI indicator, the shared rights, nothing at all where they disagree — so warning per
 * source would name fields that are never written and miss the ones that are.
 *
 * Returned in the order fields appear in the form, so the warning reads down
 * the drawer rather than in whatever order the assets happened to arrive.
 */
export function conflictingInheritedFields(
  assets: AssetResponse[],
  sourceImages: AssetResponse[],
  vocabulary: CountryVocabulary,
): InheritedField[] {
  const incoming = resolveInherited(sourceImages, vocabulary);
  return INHERITED_FROM_SOURCE.filter((field) => {
    const value = incoming[field] ?? null;
    if (isBlank(value)) return false;
    return assets.some((asset) => {
      const current = asset[field];
      return !isBlank(current) && key(current) !== key(value);
    });
  });
}

/** The conflicting fields as the names the form gives them. */
export function conflictLabels(fields: InheritedField[]): string[] {
  return fields.map((field) => METADATA_FIELD_LABELS[field]);
}
